import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useHistory, useParams } from 'react-router-dom';
import { getSWR, patch, post } from 'utils/api';
import { Box } from 'rebass';

import { useCurrentMember } from 'members/hooks/index';
import { calculatePricing } from 'app/payment/DashboardPrices';
import PaymentPage from 'app/payment/PaymentPage';
import useLocation from 'app/hooks/location';
import { getAllPackages } from 'app/form/helper';
import PaymentSuccess from 'app/payment/PaymentSuccess';
import { filterUTMParams } from 'members/helper';
import ReadyToPurchase from 'gift-certificate/ReadyToPurchase';
import Wrapper from 'gift-certificate/Wrapper';

const Paywall = () => {
  const params = useParams();
  const { id } = params
  const [step, setStep] = useState(1);
  const { push } = useHistory();
  const [redeemCode, setRedeemCode] = useState(null);

  const [orderSummary, setOrderSummary] = useState(null);
  const member = useCurrentMember();
  const { search } = useLocation();
  const [email, setEmail] = useState(member?.email);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const [quantity, setRequiresQuantity] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);

  function getUrl() {
    let param = `?group=onboarding`;
    return `/v3/child_journals/product_packages${param}`;
  }

  const { data: mainData } = useSWR(getUrl(), getSWR);

  const data = mainData?.data

  const calculatedPricing = calculatePricing(data, selectedPlan) || {};
  calculatedPricing.allPackages = getAllPackages(data, true)
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!calculatedPricing.allPackages?.paidPlans) return;
    const plan = calculatedPricing.allPackages.paidPlans.find(({ description }) => description?.includes('Hardcover'));
    if (plan && !selectedPlan) {
      setSelectedPlan(plan);
    }
  }, [calculatedPricing.allPackages?.paidPlans, selectedPlan])

  async function generateInvoice(postalCode) {
    setGeneratingInvoice(true)
    setOrderSummary(null)
    const res = await post(`/v3/product_packages/${calculatedPricing?.packageId}/invoices` +
      (quantity !== undefined ? `?quantity=${quantity}` : '') +
      (postalCode ? `&postal_code=${postalCode}` : '')
    );

    if (res.status === 200) {
      const lineItems = res?.data?.initial?.data?.relationships?.['line-items']?.data || [];
      const lineItemsDetails = lineItems.map(lineItem => ({
        ...res?.data?.initial?.included.find(({ id, type }) => id === lineItem?.id && type.includes(lineItem?.type))?.attributes,
        id: lineItem?.id,
      }));
      setOrderSummary({
        ...res?.data?.initial?.data?.attributes,
        lineItems: lineItemsDetails,
      });
      setGeneratingInvoice(false)
      return { ...res?.data?.initial?.data?.attributes, ...res?.data?.initial?.included.find(({ id, type }) => id === lineItems?.id && type.includes(lineItems?.type))?.attributes, id: res?.data?.initial?.data?.id }
    }
    else {
      setGeneratingInvoice(false)
      return false
    }
  }

  const handleStepChange = (newStep) => push(`?step=${newStep}${filterUTMParams(window?.location?.search)}`);

  useEffect(() => {
    if ((step === 1 || step === 2) && calculatedPricing?.packageId) {
      generateInvoice()
    }
  }, [step, calculatedPricing?.packageId, quantity]);

  async function firePostMemberEvent(eventName) {
    // postMemberEvent(getPrefixedEventName(eventName))
  }

  function skiptoStep() {

    push(`?step=${step + 1}${filterUTMParams(window?.location?.search)}`)

  }

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const stepFromUrl = queryParams.get('step');

    if (stepFromUrl) {
      setStep(parseInt(stepFromUrl, 10));
    } else if (!stepFromUrl)
      push(`?step=${1}${filterUTMParams(window?.location?.search)}`)

    // if (stepFromUrl === 0)
    //   firePostMemberEvent('intro')
  }, [search])

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <ReadyToPurchase
            nextButtonText="Continue"
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            allPackages={calculatedPricing.allPackages}
            hideSkip
            firePostMemberEvent={firePostMemberEvent}
            quantity={quantity}
            redeemCode={redeemCode}
            setRedeemCode={setRedeemCode}
            member={member}
            setStatus={setStatus}
            status={status}
            email={email}
            isFromPaywall
            setEmail={setEmail}
            calculatedPricing={calculatedPricing}
            setRequiresQuantity={setRequiresQuantity}
            onStepUp={async () => {
              if (status?.type !== 'success') {
                skiptoStep()
              } else {
                if (redeemCode)
                  await patch(`/v3/commerce/gift_certificates/${redeemCode}/claim`)

                push(`?step=${3}${filterUTMParams(window?.location?.search)}`)
              }
            }}
            generateInvoice={generateInvoice}
            onStepDown={() => push(`/child-journals/${id}`)}
            orderSummary={orderSummary}
          />
        )

      case 2:
        return <Wrapper
          hideSkip
          percentComplete={100}
          showFooter={false}
        // mainImage={isFromMobile ? mobilePurchaseGiftBFY : purchaseGiftBFY}
        >
          <PaymentPage
            generatingInvoice={generatingInvoice}
            isForMyBaby={false}
            backText="Back"
            onStepUp={skiptoStep}
            onBack={() => handleStepChange(step - 1)}
            quantity={quantity}
            calculatedPricing={calculatedPricing}
            selectedPlan={selectedPlan}
            orderSummary={orderSummary}
            generateInvoice={generateInvoice}
            firePostMemberEvent={firePostMemberEvent}
            currentChildJournalId={id}
          />
        </Wrapper>
      case 3:
        return <PaymentSuccess
          showFooter={false}
          isForMyBaby={false}
          redemptionStatus={status}
          quantity={quantity}
          orderSummary={orderSummary}
          calculatedPricing={calculatedPricing}
          currentChildJournalId={id}
        />

      default:
        return null;
    }
  };

  return <Box sx={{ position: 'fixed', zIndex: 9, top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', backgroundColor: 'aliceBlue' }} width="100vw" height="100vh">
    {renderStep()}
  </Box>
};

export default Paywall;
